import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, DatePicker, Select, Button, Row, Col, Statistic, Table, Typography, Empty, message, Space
} from 'antd';
import { useAuth } from '../../context/AuthContext';
import {
    SearchOutlined, PieChartOutlined, ClockCircleOutlined, PhoneOutlined, FieldTimeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { reportService, pbxReportCatalogService } from '../../services/api';
// Chart.js components will be needed. Simplified version or using Recharts/Antd if available?
// Given context, we might not have a chart lib installed. I will use simple CSS pie or just text if no lib.
// Wait, user asked for "grafico pie". I will use 'recharts' if available or a simple CSS implementation.
// Let's check package.json first? No, I should assume standard environment or check.
// I will attempt to use 'recharts' as it is common. If not, I can handle error or use CSS.
// SAFE BET: Use simple CSS conic-gradient for Pie Chart to avoid dependency issues if not installed.
// actually, let's try to import Recharts, if it fails, I'll fix it. But I don't see package.json.
// I will implement a custom CSS Pie Chart component to be safe and dependency-free.

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const formatDuration = (s) => {
    if (s === null || s === undefined) return '0s';
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
    return parts.join(' ');
};

const SimplePieChart = ({ data, valueFormatter = formatDuration }) => {
    // data: [{ name, value, color }]
    const total = data.reduce((acc, cur) => acc + cur.value, 0);
    if (total === 0) return <Empty description="Sin datos" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

    let currentAngle = 0;
    const segments = data.map(item => {
        const percentage = (item.value / total) * 100;
        const angle = (item.value / total) * 360;
        const segment = `${item.color} 0 ${angle}deg`;
        // conic-gradient logic is complex for multi-segment without accumulating.
        // Simplified:
        const start = currentAngle;
        currentAngle += angle;
        return `${item.color} ${start}deg ${currentAngle}deg`;
    });

    const background = `conic-gradient(${segments.join(', ')})`;

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <div style={{
                width: 200, height: 200, borderRadius: '50%',
                background: background,
                position: 'relative',
                border: '1px solid #f0f0f0'
            }} />
            <div style={{ marginTop: 20, display: 'flex', gap: 15, flexWrap: 'wrap', justifyContent: 'center' }}>
                {data.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: 12, height: 12, backgroundColor: d.color, marginRight: 8, borderRadius: 2 }} />
                        <Text>{d.name}: {valueFormatter(d.value)} ({((d.value/total)*100).toFixed(1)}%)</Text>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ServiceStatsReport = () => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState(null);
    
    // Filters state
    const [filters, setFilters] = useState({
        date_range: [dayjs().startOf('month'), dayjs().endOf('month')],
        campaign_id: undefined,
    });
    
    const [campaigns, setCampaigns] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    useEffect(() => {
        const fetchCampaigns = async () => {
             const res = await pbxReportCatalogService.getCampaigns();
             if (res.status === 'success') setCampaigns(res.data);
        };
        fetchCampaigns();
    }, []);

    const fetchData = async () => {
        if (!filters.campaign_id) {
            message.warning("Debe seleccionar una campaña");
            return;
        }
        
        setLoading(true);
        setHasSearched(true);
        try {
            const params = {
                campaign_id: filters.campaign_id,
                date_range: filters.date_range ? filters.date_range.map(d => d.format('YYYY-MM-DD')).join(',') : null
            };
            const response = await reportService.getServiceStats(params);
            if (response.status === 'success') {
                setStats(response.data);
            } else {
                setStats(null);
            }
        } catch (error) {
            console.error(error);
            message.error("Error al cargar estadísticas");
            setStats(null);
        } finally {
            setLoading(false);
        }
    };

    const renderTimeTable = (title, metrics) => {
         const dataSource = [
             { key: '1', metric: 'Tiempo Máximo', value: formatDuration(metrics?.max) },
             { key: '2', metric: 'Tiempo Promedio', value: formatDuration(metrics?.avg ? Math.round(metrics.avg) : 0) },
             { key: '3', metric: 'Tiempo Mínimo', value: formatDuration(metrics?.min) },
         ];

         const columns = [
             { title: 'Métrica', dataIndex: 'metric', key: 'metric' },
             { title: 'Valor', dataIndex: 'value', key: 'value', align: 'right' },
         ];

         return (
             <Table 
                columns={columns} 
                dataSource={dataSource} 
                pagination={false} 
                title={() => <b>{title}</b>}
                size="small"
                bordered
             />
         );
    };

    return (
        <div className="fade-in">
             <Card bordered={false} style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Space wrap>
                    <Select
                        placeholder="Seleccione Campaña"
                        value={filters.campaign_id}
                        onChange={(val) => setFilters({ ...filters, campaign_id: val })}
                        style={{ width: 250 }}
                        optionFilterProp="children"
                        showSearch
                    >
                        {campaigns.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                    </Select>
                    <RangePicker
                        value={filters.date_range}
                        onChange={(val) => setFilters({ ...filters, date_range: val })}
                        placeholder={['Inicio', 'Fin']}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={fetchData} loading={loading}>
                        Mostrar datos
                    </Button>
                </Space>
             </Card>

             {!hasSearched || !stats || !stats.totals ? (
                 <Empty description={hasSearched ? "No se encontraron datos" : "Seleccione una campaña para ver estadísticas"} />
             ) : (
                 <Space direction="vertical" size="large" style={{ width: '100%' }}>
                     {/* Top Cards */}
                     <Row gutter={[16, 16]}>
                         <Col xs={24} sm={12} md={6}>
                             <Card>
                                 <Statistic 
                                    title="Total Llamadas" 
                                    value={stats.totals.calls} 
                                    prefix={<PhoneOutlined />} 
                                    valueStyle={{ color: '#1890ff' }}
                                 />
                             </Card>
                         </Col>
                         <Col xs={24} sm={12} md={6}>
                             <Card>
                                 <Statistic 
                                    title="Tiempo Total (Duración)" 
                                    value={formatDuration(stats.totals.duration)} 
                                    prefix={<ClockCircleOutlined />} 
                                 />
                             </Card>
                         </Col>
                         <Col xs={24} sm={12} md={6}>
                             <Card>
                                 <Statistic 
                                    title="Tiempo Hablado Total" 
                                    value={formatDuration(stats.totals.talk)} 
                                    prefix={<FieldTimeOutlined />} 
                                    valueStyle={{ color: '#52c41a' }}
                                 />
                             </Card>
                         </Col>
                         <Col xs={24} sm={12} md={6}>
                             <Card>
                                 <Statistic 
                                    title="Tiempo Espera Total" 
                                    value={formatDuration(stats.totals.wait)} 
                                    prefix={<ClockCircleOutlined />} 
                                    valueStyle={{ color: '#faad14' }}
                                 />
                             </Card>
                         </Col>
                     </Row>

                     {/* Middle Row: Pie Chart and Tables */}
                     <Row gutter={[24, 24]}>
                         <Col xs={24} lg={6}>
                             <Card title={<Space><PieChartOutlined /> Distribución de Llamadas</Space>} style={{ height: '100%' }}>
                                 <SimplePieChart 
                                     data={[
                                         { name: 'Contestadas', value: stats.totals.answered, color: '#52c41a' },
                                         { name: 'Abandonadas', value: stats.totals.abandoned, color: '#f5222d' }, // red for abandoned
                                     ]}
                                     valueFormatter={(val) => val} // Simple number
                                 />
                             </Card>
                         </Col>
                         <Col xs={24} lg={6}>
                             <Card title={<Space><PieChartOutlined /> Distribución de Tiempo</Space>} style={{ height: '100%' }}>
                                 <SimplePieChart 
                                     data={[
                                         { name: 'Tiempo Hablado', value: stats.totals.talk, color: '#52c41a' },
                                         { name: 'Tiempo Espera', value: stats.totals.wait, color: '#faad14' },
                                     ]}
                                     valueFormatter={formatDuration}
                                 />
                             </Card>
                         </Col>
                         <Col xs={24} lg={6}>
                             <Card style={{ height: '100%' }}>
                                 {renderTimeTable("Duración de llamadas (Hablado)", stats.talk_time)}
                             </Card>
                         </Col>
                         <Col xs={24} lg={6}>
                             <Card style={{ height: '100%' }}>
                                 {renderTimeTable("Tiempos de espera", stats.wait_time)}
                             </Card>
                         </Col>
                     </Row>
                 </Space>
             )}
        </div>
    );
};

export default ServiceStatsReport;
